import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FileText, Car, BedDouble, Package,
  ClipboardList, MapPin, Plane,
  ShieldCheck, Headphones, Globe,
  ArrowRight, CheckCircle2, X, Send, Check,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { useCatalog } from '../context/CatalogContext';
import { getTravelDestinationImage } from '../lib/imageOverrides';

/* removed: unused WA_NUMBER_FALLBACK */

const SERVICES = [
  {
    icon: FileText,
    title: 'Visa Support',
    desc: 'Fast, simple visa assistance and entry guidance for your healthcare visit.',
    to: '/travel-hub/visa-support',
  },
  {
    icon: Car,
    title: 'Transport',
    desc: 'Airport pickup and clinic transfers arranged for you — no stress on arrival.',
    to: '/travel-hub/transport',
  },
  {
    icon: BedDouble,
    title: 'Stay & Recovery',
    desc: 'We recommend trusted hotels and recovery-friendly stays near your clinic.',
    to: '/travel-hub/stay-recovery',
  },
  {
    icon: Package,
    title: 'Medical Packages',
    desc: 'Optional treatment and travel support bundles tailored to your journey.',
    to: '/travel-hub/medical-packages',
  },
];

const DESTINATIONS = [
  {
    city: 'Ho Chi Minh City',
    tag: 'Most popular',
    img: 'https://images.unsplash.com/photo-1592114631383-89cafc376ba5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
  {
    city: 'Hanoi',
    tag: 'Cultural capital',
    img: 'https://images.unsplash.com/photo-1758104372690-0e14bc4dec5c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
  {
    city: 'Da Nang',
    tag: 'Coastal city',
    img: 'https://images.unsplash.com/photo-1722171742010-bef442b25f3f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
  {
    city: 'Nha Trang',
    tag: 'Beach retreat',
    img: 'https://images.unsplash.com/photo-1767423656732-ca909689612b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  },
];

const STEPS = [
  {
    icon: ClipboardList,
    step: '01',
    title: 'Tell us your treatment',
    desc: 'Share your healthcare needs and preferred travel dates. We handle the rest.',
  },
  {
    icon: MapPin,
    step: '02',
    title: 'We recommend clinics & travel support',
    desc: 'Get matched with verified clinics and personalised travel, visa, and stay arrangements.',
  },
  {
    icon: Plane,
    step: '03',
    title: 'You confirm and travel stress-free',
    desc: 'Our team supports you every step — from booking confirmation to your safe return.',
  },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, text: 'Verified partners' },
  { icon: Headphones, text: 'English support' },
  { icon: Globe, text: 'End-to-end guidance' },
];

const TravelHubPage = () => {
  const { contactInfo } = useContactInfo();
  const { overrides } = useCatalog();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', treatment: '', travelDates: '', city: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);

  const CITIES = ['Ho Chi Minh City', 'Hanoi', 'Da Nang', 'Hoi An', 'Nha Trang', 'Other'];

  const openModal = () => { setSubmitted(false); setShowModal(true); };

  const handleSubmit = () => {
    if (!form.fullName.trim() || !form.email.trim()) return;
    const msg = [
      `Hello, I found VietClinics and would like to plan a medical trip.`,
      `Name: ${form.fullName}`,
      form.phone && `Phone: ${form.phone}`,
      form.treatment && `Treatment/Purpose: ${form.treatment}`,
      form.travelDates && `Travel Dates: ${form.travelDates}`,
      form.city && `Destination: ${form.city}`,
      form.notes && `Notes: ${form.notes}`,
      `Email: ${form.email}`,
    ].filter(Boolean).join('\n');
    const href = getWhatsAppHref(contactInfo, msg);
    if (href) window.open(href, '_blank');
    setSubmitted(true);
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Travel Hub — Plan Your Medical Trip to Vietnam | VietClinics</title>
        <meta name="description" content="Plan your healthcare trip to Vietnam with VietClinics — visa support, airport transport, hotel & recovery stays and medical travel packages, all in one place." />
        <link rel="canonical" href="https://vietclinics.com/travel-hub" />
      </Helmet>
      <Header />

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-[#1E5BA8] via-[#1FA7A1] to-[#26A9E0] py-20 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

            {/* Left */}
            <div className="flex-1 text-center lg:text-left">
              <span className="inline-block text-white/70 text-sm font-semibold tracking-widest uppercase mb-4">
                Travel Hub
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Travel & Stay in<br />Vietnam —<br className="hidden md:block" /> Simplified
              </h1>
              <p className="text-lg text-white/85 mb-8 max-w-lg leading-relaxed">
                Plan your healthcare journey with visa support, transport, and recovery
                guidance — all in one place.
              </p>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-10">
                {TRUST_BADGES.map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2">
                    <Icon className="h-4 w-4 text-white/90" />
                    <span className="text-sm text-white font-medium">{text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={openModal}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-[#1FA7A1] font-bold rounded-xl text-base hover:bg-gray-50 shadow-xl transition-all"
                  data-testid="hero-plan-trip-btn"
                >
                  Plan Your Trip <ArrowRight className="h-4 w-4" />
                </button>
                {contactInfo?.whatsappNumber && (
                  <button
                    onClick={openModal}
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#25D366] text-white font-bold rounded-xl text-base hover:bg-[#1fb355] shadow-xl transition-all"
                    data-testid="hero-whatsapp-btn"
                  >
                    <svg className="h-5 w-5 fill-white" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Chat on WhatsApp
                  </button>
                )}
              </div>
            </div>

            {/* Right — city image */}
            <div className="flex-shrink-0 w-full lg:w-[440px]">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
                <img
                  src="https://images.pexels.com/photos/7904480/pexels-photo-7904480.jpeg?auto=compress&cs=tinysrgb&w=940"
                  alt="Medical travel in Vietnam"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1FA7A1]/40 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="w-9 h-9 bg-[#1FA7A1]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-[#1FA7A1]" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Your trip, fully supported</p>
                      <p className="text-xs text-gray-500">Visa · Transfers · Recovery stays</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ───────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Everything You Need, Handled
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              We coordinate your entire healthcare travel experience so you can focus on your health.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {SERVICES.map(({ icon: Icon, title, desc, to }, i) => (
              <Link
                key={i}
                to={to}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all block group"
                data-testid={`service-card-${i}`}
                aria-label={`${title} for medical trips in Vietnam`}
              >
                <div className="w-12 h-12 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center mb-5">
                  <Icon className="h-6 w-6 text-[#1FA7A1]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{desc}</p>
                <span className="text-xs font-semibold text-[#1FA7A1] flex items-center gap-1 group-hover:gap-2 transition-all">
                  {title} in Vietnam <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Destinations ───────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Where Are You Headed?
            </h2>
            <p className="text-gray-500">We support your healthcare journey across Vietnam</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10 max-w-5xl mx-auto">
            {DESTINATIONS.map(({ city, tag, img }, i) => (
              <Link
                key={i}
                to="/#request-appointment"
                className="group relative rounded-2xl overflow-hidden aspect-[3/4] shadow-sm hover:shadow-xl transition-all block"
                data-testid={`destination-${i}`}
              >
                <img
                  src={getTravelDestinationImage(overrides, city, img)}
                  alt={city}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.src = img; }}
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block text-xs font-semibold text-white/75 bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1 mb-1.5">
                    {tag}
                  </span>
                  <h3 className="text-white font-bold text-base leading-tight">{city}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              How It Works
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Three simple steps to a stress-free healthcare trip.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {STEPS.map(({ icon: Icon, step, title, desc }, i) => (
              <div key={i} className="relative text-center" data-testid={`step-${i + 1}`}>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+2.5rem)] w-[calc(100%-5rem)] h-px bg-[#1FA7A1]/25" />
                )}
                <div className="w-16 h-16 bg-[#1FA7A1]/8 rounded-2xl border border-[#1FA7A1]/20 flex items-center justify-center mx-auto mb-5 relative z-10">
                  <Icon className="h-7 w-7 text-[#1FA7A1]" />
                </div>
                <span className="text-xs font-bold text-[#1FA7A1] tracking-widest uppercase">Step {step}</span>
                <h3 className="font-bold text-gray-900 mt-1.5 mb-2 text-base">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-[#1E5BA8] to-[#1FA7A1]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Need help planning your trip?
          </h2>
          <p className="text-white/80 mb-10 max-w-xl mx-auto text-lg">
            Our team will guide you step-by-step — from clinic to arrival.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {contactInfo?.whatsappNumber && (
              <button
                onClick={openModal}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] text-white font-bold rounded-xl text-base hover:bg-[#1fb355] shadow-xl transition-all"
                data-testid="cta-whatsapp-btn"
              >
                <svg className="h-5 w-5 fill-white" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Plan My Trip
              </button>
            )}
            <Link
              to="/#request-appointment"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#1FA7A1] font-bold rounded-xl text-base hover:bg-gray-50 shadow-xl transition-all"
              data-testid="cta-contact-btn"
            >
              Request Appointment <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Travel Inquiry Modal ──────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden flex flex-col max-h-[92vh]">

            {/* Header */}
            <div className="bg-gradient-to-r from-[#1E5BA8] to-[#1FA7A1] p-5 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">Plan Your Medical Trip</h2>
                  <p className="text-white/80 text-sm mt-0.5">Tell us your needs — we'll guide you</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                  data-testid="close-travel-modal"
                >
                  <X className="h-4 w-4 text-white" />
                </button>
              </div>
            </div>

            {submitted ? (
              /* Success state */
              <div className="p-6 text-center flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                  <Check className="h-8 w-8 text-[#25D366]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">WhatsApp Opened</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    Your enquiry has been sent to our team via WhatsApp.<br />
                    We'll reply within a few hours to start planning your trip.
                  </p>
                </div>
                <Button
                  onClick={() => setShowModal(false)}
                  className="w-full bg-[#1FA7A1] hover:bg-[#178f8a] text-white py-5 font-bold"
                >
                  Back to Travel Hub
                </Button>
              </div>
            ) : (
              /* Form */
              <div className="p-6 space-y-4 overflow-y-auto">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Full Name <span className="text-red-400">*</span>
                  </label>
                  <Input
                    placeholder="Your full name"
                    value={form.fullName}
                    onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                    data-testid="travel-fullname"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    data-testid="travel-email"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number</label>
                  <Input
                    type="tel"
                    placeholder="+84 xxx xxx xxxx"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    data-testid="travel-phone"
                  />
                </div>

                {/* Treatment / Purpose */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Treatment / Purpose</label>
                  <Input
                    placeholder="e.g. Dental implants, cosmetic surgery..."
                    value={form.treatment}
                    onChange={(e) => setForm((p) => ({ ...p, treatment: e.target.value }))}
                    data-testid="travel-treatment"
                  />
                </div>

                {/* Preferred Travel Dates */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Preferred Travel Dates</label>
                  <Input
                    placeholder="e.g. March 2026, flexible, ASAP..."
                    value={form.travelDates}
                    onChange={(e) => setForm((p) => ({ ...p, travelDates: e.target.value }))}
                    data-testid="travel-dates"
                  />
                </div>

                {/* Destination City */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Preferred City</label>
                  <select
                    value={form.city}
                    onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                    className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/50"
                    data-testid="travel-city"
                  >
                    <option value="">Select a city (optional)</option>
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Additional Notes <span className="text-gray-400 font-normal text-xs">(optional)</span>
                  </label>
                  <textarea
                    placeholder="Any specific requirements, medical history notes, or questions..."
                    value={form.notes}
                    onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-lg resize-none h-20 text-sm focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/50"
                    data-testid="travel-notes"
                  />
                </div>

                <div className="flex items-center gap-4 pt-1 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-[#2ECC71]" /> Free trip planning</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-[#2ECC71]" /> No commitment needed</span>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={!form.fullName.trim() || !form.email.trim()}
                  className="w-full bg-[#25D366] hover:bg-[#1fb355] text-white py-6 text-base font-bold disabled:opacity-50"
                  data-testid="travel-submit-btn"
                >
                  <span className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Send via WhatsApp
                  </span>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default TravelHubPage;
